/**
 * ========================================
 * AI HELP ASSISTANT SERVICE
 * ========================================
 * API calls for the AI Help Assistant module.
 * Endpoints: /api/v1/help/*
 */

import { apiClient } from './client';
import { handleAPIError } from './error-handler';
import type { ChatAnswer, ChatSession, FeedbackRequest, EscalateRequest, Suggestion } from '@/types/aiHelp';
import type { Ticket } from '@/types/ticket';

interface BackendEnvelope<T> {
  status: string;
  status_code?: number;
  message: string;
  data: T;
}

class AIHelpService {
  private baseUrl = '/api/v1/help';

  async chat(message: string, sessionId?: string): Promise<ChatAnswer> {
    try {
      const response = await apiClient.post<BackendEnvelope<ChatAnswer>>(`${this.baseUrl}/chat`, {
        message,
        session_id: sessionId,
      });
      return response.data.data;
    } catch (error) {
      handleAPIError(error);
    }
  }

  async history(): Promise<ChatSession[]> {
    try {
      const response = await apiClient.get<BackendEnvelope<ChatSession[]>>(`${this.baseUrl}/history`);
      return response.data.data;
    } catch (error) {
      handleAPIError(error);
    }
  }

  async getSession(id: string): Promise<ChatSession> {
    try {
      const response = await apiClient.get<BackendEnvelope<ChatSession>>(`${this.baseUrl}/session/${id}`);
      return response.data.data;
    } catch (error) {
      handleAPIError(error);
    }
  }

  async feedback(payload: FeedbackRequest): Promise<void> {
    try {
      await apiClient.post(`${this.baseUrl}/feedback`, payload);
    } catch (error) {
      handleAPIError(error);
    }
  }

  async escalate(payload: EscalateRequest): Promise<Ticket> {
    try {
      const response = await apiClient.post<BackendEnvelope<Ticket>>(`${this.baseUrl}/escalate`, payload);
      return response.data.data;
    } catch (error) {
      handleAPIError(error);
    }
  }

  async suggestions(): Promise<Suggestion[]> {
    try {
      const response = await apiClient.get<BackendEnvelope<Suggestion[]>>(`${this.baseUrl}/suggestions`);
      return response.data.data;
    } catch (error) {
      handleAPIError(error);
    }
  }
}

export const aiHelpService = new AIHelpService();
export default aiHelpService;
